'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Navbar from '@/components/common/Navbar';

interface Testimonial {
  id: string;
  customer_name: string;
  service_type: string | null;
  rating: number;
  testimonial_text: string;
  video_url: string | null;
  is_featured: boolean;
}

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_approved', true)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="StarIcon"
        size={20}
        className={i < rating ? 'text-yellow-400' : 'text-gray-300'}
        variant="solid"
      />
    ));
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/30">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Customer Testimonials</h1>
            <p className="text-xl text-white/90">Hear what our satisfied customers have to say about our cleaning services.</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-green-50 via-white to-green-50 border-b border-green-100">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">4.9★</div>
              <p className="text-muted-foreground">Average Rating</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">1,200+</div>
              <p className="text-muted-foreground">Happy Customers</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">150+</div>
              <p className="text-muted-foreground">5-Star Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Content */}
      <section className="py-20 section-gradient-mid dot-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Icon name="ArrowPathIcon" size={40} className="animate-spin text-green-600 mx-auto" />
              <p className="text-muted-foreground mt-4">Loading testimonials...</p>
            </div>
          )}

          {/* Testimonials Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-muted-foreground">No testimonials available yet.</p>
                </div>
              ) : (
                testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1.5 hover:border-[#4CAF50] transition-all duration-300 ${
                      testimonial.is_featured ? 'border-2 border-green-500' : 'border border-transparent'
                    }`}
                  >
                    {testimonial.is_featured && (
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="StarIcon" size={16} className="text-green-600" variant="solid" />
                        <span className="text-sm font-semibold text-green-600">Featured Review</span>
                      </div>
                    )}

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-4">
                      {renderStars(testimonial.rating)}
                    </div>

                    {/* Testimonial Text */}
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      "{testimonial.testimonial_text}"
                    </p>

                    {/* Customer Info */}
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-semibold text-foreground">{testimonial.customer_name}</p>
                      {testimonial.service_type && (
                        <p className="text-sm text-muted-foreground">
                          {testimonial.service_type.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                      )}
                    </div>

                    {/* Video Testimonial */}
                    {testimonial.video_url && (
                      <div className="mt-4">
                        <a
                          href={testimonial.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold text-sm"
                        >
                          <Icon name="PlayCircleIcon" size={20} />
                          Watch Video
                        </a>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32] via-green-600 to-[#4CAF50] text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Join Our Happy Customers</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Experience the same exceptional service that earned us these glowing reviews.
          </p>
          <Link
            href="/homepage#booking"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 hover:shadow-lg transition-all duration-200 shadow-lg"
          >
            Book Your Cleaning
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}